/**
 * CircleReport — the "Your inner circle" card (/circle).
 *
 * Data comes from the correspondents rollup via the sent scan: CLOSEST
 * ranked by two-way volume with cadence + recency, a GOING QUIET section
 * (real cadence that stopped) on an accent rail, and a stat strip.
 */

export interface CircleEntry {
  name: string;
  email: string;
  sentCount: number;
  receivedCount: number;
  lastSeenISO: string | null;
  meetings: number;
}

export interface CircleData {
  closest: CircleEntry[];
  goingQuiet: CircleEntry[];
  regulars: number;
  meetRegularly: number;
}

function cadence(c: CircleEntry): string {
  const total = c.sentCount + c.receivedCount;
  const meets = c.meetings >= 2 ? ' + meets' : '';
  if (total >= 100) return `Email daily${meets}`;
  if (total >= 40) return `Most weeks${meets}`;
  if (total >= 12) return `Monthly${meets}`;
  return `Occasional${meets}`;
}

function lastSeen(iso: string | null, now: Date): string {
  if (!iso) return '';
  const days = Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.round(days / 7)} wks ago`;
  return `${Math.round(days / 30)} mo ago`;
}

function quietFor(iso: string | null, now: Date): string {
  if (!iso) return '';
  const months = Math.max(1, Math.round((now.getTime() - new Date(iso).getTime()) / (30 * 86_400_000)));
  return `quiet ${months} mo`;
}

const initials = (name: string) =>
  name.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');

export function CircleReport({ circle }: { circle: CircleData }) {
  const now = new Date();
  return (
    <div className="otr orr">
      <div className="otr-label">People</div>
      <div className="otr-heading">Your inner circle</div>
      <div className="otr-stats">
        <div className="otr-stat">
          <div className="otr-stat-n">{circle.regulars}</div>
          <div className="otr-stat-label">regular contacts</div>
        </div>
        <div className="otr-stat">
          <div className="otr-stat-n">{circle.meetRegularly}</div>
          <div className="otr-stat-label">meet regularly</div>
        </div>
        <div className="otr-stat">
          <div className="otr-stat-n orr-accent">{circle.goingQuiet.length}</div>
          <div className="otr-stat-label orr-accent">going quiet</div>
        </div>
      </div>

      {circle.closest.length > 0 && (
        <>
          <div className="orr-section-label">Closest</div>
          {circle.closest.map((c) => (
            <div className="ocr-row" key={c.email}>
              <span className="ocr-avatar">{initials(c.name)}</span>
              <div className="ocr-main">
                <div className="orr-name">{c.name}</div>
                <div className="orr-sub">{c.email}</div>
              </div>
              <div className="ocr-cadence">{cadence(c)}</div>
              <div className="orr-reg-last">{lastSeen(c.lastSeenISO, now)}</div>
            </div>
          ))}
        </>
      )}

      {circle.goingQuiet.length > 0 && (
        <>
          <div className="orr-section-label orr-accent">Going quiet</div>
          <div className="orr-upcoming">
            {circle.goingQuiet.map((c) => (
              <div className="ocr-row" key={c.email}>
                <span className="ocr-avatar">{initials(c.name)}</span>
                <div className="ocr-main">
                  <div className="orr-name">{c.name}</div>
                  <div className="orr-sub">was {cadence(c).toLowerCase()}</div>
                </div>
                <div className="orr-rel orr-accent">{quietFor(c.lastSeenISO, now)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
